import { useId, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CollectionVersionSummary } from "../../../../api/content/contentDecoders";
import { itemIdOf } from "../../../../api/content/processDefinitions";
import { listContentVersions, listWorkflows, type WorkflowListing, type WorkflowPurpose, type WorkflowSummary } from "../../../../api/content/workflowsApi";
import { listStartWorkflows } from "../../../../api/threads/threadsApi";
import ErrorNotice from "../../../../errors/ErrorNotice";
import ConnectorSetupButton from "../../../connectors/setup/ConnectorSetupButton";
import { useLoad } from "../../../../shared/loading/useLoad";
import { formatCollectionVersionRef, parseCollectionVersionRef, type CollectionVersionRef } from "../../../../shared/references/contentReferences";
import { useSubmission } from "../../../../shared/state/useSubmission";
import AppButton from "../../../../ui/buttons/AppButton";
import LoadStatus from "../../../../ui/feedback/LoadStatus";
import { FORM_HELP_TEXT_CLASS } from "../../../../ui/styles/formStyles";
import { useCorpusSelection } from "../../../sources/corpus/corpusSelectionContext";
import EarlierRuns from "./EarlierRuns";
import RunVersions from "./RunVersions";
import type { PreparationViewProps } from "../preparationView";
import { startRun } from "../startCommands";
import type { RunIntent, ThreadSubject } from "../threadIntent";
import DocumentSetField from "./DocumentSetField";
import { entryBindings } from "./entrySelection";
import { runTitle } from "./runTitle";
import StartPlan from "./StartPlan";
import { startBlockers } from "./startBlockers";
import { useFocusOnInputs } from "./useFocusOnInputs";
import { usePreparedStart } from "./usePreparedStart";
import WorkflowChoice from "./WorkflowChoice";
import { StartRoutes } from "../../llmChoice/StartRoutes";
import { StartRoutesContext } from "../../llmChoice/startRoutesContext";
import type { StartRouteDraft } from "../../llmChoice/startRouteDraft";
import { useStartRouteNeed } from "../../llmChoice/useStartRouteNeed";

/** A project before its first change set: no content version, so no Workflows; a free Thread opens the content. */
const NO_CONTENT_YET: Promise<WorkflowListing | null> = Promise.resolve(null);
/** Started from no documents: every Workflow is offered. */
const ALL_FIT: Promise<ReadonlySet<string> | null> = Promise.resolve(null);
const FREE_THREAD = "";
const NO_VALUES: Readonly<Record<string, unknown>> = {};
const NO_ENTRIES: readonly ThreadSubject[] = [];
/** Shipped Workflows' purposes whose inputs are all chosen in this preparation, so they start here as well. */
const STARTS_HERE: ReadonlySet<WorkflowPurpose> = new Set<WorkflowPurpose>(["accept_results"]);

/** The entries and the corpus version that holds them; the corpus is `null` exactly when there are no entries. */
interface EntrySelection {
  readonly entries: readonly ThreadSubject[];
  readonly corpus: CollectionVersionRef | null;
}

/**
 * Preparing a run (F31, F28, F30; SB-08, SB-14): the Workflow, then its inputs, then Start.
 * The content version is a detail: the newest unless chosen. The Workflows offered are the
 * content version's, in the server's order; which of them fit the documents started from is
 * the server's (`listStartWorkflows`, F123), as is whether a picked one is ready (`prepare`,
 * ADR-0032 D7), not a rule of this view. A picked Workflow shows its steps as the
 * server prepares them for this start, with the values the start gives them (Johannes, 27
 * Oct, point 8). Choosing one moves the focus to its inputs (F98); the documents are asked
 * only where the Workflow binds a subject (F99), and Start waits until the server finds the
 * start ready, naming what is missing (F101).
 * A Workflow chosen before ("Run this Workflow…") leads as the one being run (F52).
 */
export default function RunPreparation({ intent, requestKey, onStarted }: PreparationViewProps<RunIntent>) {
  const { t } = useTranslation();
  const { projectId } = intent;
  const loadVersions = useCallback((signal: AbortSignal) => listContentVersions(projectId, signal), [projectId]);
  const { state: versions, reload } = useLoad(loadVersions);
  // The version the intent names, if any (a page's model version); otherwise the newest.
  const [chosenVersion, setChosenVersion] = useState<string | null>(intent.contentVersion ?? null);
  // The documents and the Workflow (by item) belong to this preparation; another content version keeps them.
  // The entries with the corpus version that holds them, one value (BA16-07): the action that brought
  // them named it, a pick sets it, and corpus browsing elsewhere never rebinds it.
  const [selection, setSelection] = useState<EntrySelection>(() => ({ entries: intent.entries, corpus: intent.corpusVersion === null ? null : parseCollectionVersionRef(intent.corpusVersion) }));
  const [pickedItem, setPickedItem] = useState<string | null>(intent.workflow === null ? null : itemIdOf(intent.workflow));

  if (versions.status !== "loaded") return <LoadStatus state={versions} loadingLabel={t("threads.prepare.run.loadingVersions")} onRetry={reload} />;
  const version = versions.value.find((candidate) => candidate.reference === chosenVersion) ?? versions.value[0] ?? null;
  return (
    <RunSetup
      key={version?.commit ?? ""}
      intent={intent}
      requestKey={requestKey}
      onStarted={onStarted}
      versions={versions.value}
      version={version}
      onVersionChange={setChosenVersion}
      entries={selection.entries}
      entryCorpus={selection.corpus}
      onEntriesChange={(changed, picked) => { setSelection((held) => ({ entries: changed, corpus: changed.length === 0 ? null : (picked ?? held.corpus) })); }}
      pickedItem={pickedItem}
      onPick={setPickedItem}
    />
  );
}

interface RunSetupProps extends PreparationViewProps<RunIntent> {
  versions: readonly CollectionVersionSummary[];
  /** `null`: the project has no content version yet. */
  version: CollectionVersionSummary | null;
  onVersionChange: (reference: string) => void;
  entries: readonly ThreadSubject[];
  /** The corpus version that holds the entries; `null` while there are none. */
  entryCorpus: CollectionVersionRef | null;
  /** `picked`: the corpus version of an added entry; `null` when one was removed. */
  onEntriesChange: (entries: readonly ThreadSubject[], picked: CollectionVersionRef | null) => void;
  /** The chosen Workflow's item ID, `FREE_THREAD` for none, `null` before a choice. */
  pickedItem: string | null;
  onPick: (item: string) => void;
}

function RunSetup({ intent, requestKey, onStarted, versions, version, onVersionChange, entries, entryCorpus, onEntriesChange, pickedItem, onPick }: RunSetupProps) {
  const commit = version?.commit ?? null;
  const { t } = useTranslation();
  const headingId = useId();
  const { projectId } = intent;
  const { corpora, selectedCorpus } = useCorpusSelection();
  const loadWorkflows = useCallback(
    (signal: AbortSignal) => (commit === null ? NO_CONTENT_YET : listWorkflows(projectId, commit, signal)),
    [projectId, commit],
  );
  const { state: listing, reload } = useLoad(loadWorkflows);
  const { state: submission, submit } = useSubmission();
  // Values given for one Workflow's inputs: another Workflow starts empty.
  const [given, setGiven] = useState<{ readonly forItem: string | null; readonly values: Readonly<Record<string, unknown>> }>({ forItem: null, values: NO_VALUES });
  const values = given.forItem === pickedItem ? given.values : NO_VALUES;
  // The LLMs chosen for one Workflow (F106, TR-A2-04): another Workflow's steps start over.
  const [chosenRoutes, setChosenRoutes] = useState<{ readonly forItem: string | null; readonly draft: StartRouteDraft | null } | null>(null);
  // `undefined` until the start's PCs are read for this Workflow.
  const routeDraft = chosenRoutes?.forItem === pickedItem ? chosenRoutes.draft : undefined;
  const onRouteDraftChange = useCallback((draft: StartRouteDraft | null) => { setChosenRoutes({ forItem: pickedItem, draft }); }, [pickedItem]);
  const contentVersion = listing.status === "loaded" ? (listing.value?.version ?? null) : null;
  // The Thread binds the corpus its entries are in; only without entries, the corpus worked in.
  const runCorpus = entryCorpus ?? selectedCorpus?.latestVersion ?? null;
  const corpusVersion = runCorpus === null ? null : formatCollectionVersionRef(runCorpus);
  const bindings = useMemo(
    () => (contentVersion === null ? null : { content_version: contentVersion, corpus_version: corpusVersion, ...entryBindings(entries) }),
    [contentVersion, corpusVersion, entries],
  );
  // Started from documents, only the Workflows that take them are offered, before any pick
  // (F28, F123): module B decides which, as it decides Start's reasons; without documents, all.
  const startedFrom = entries.length === 0 ? null : bindings;
  const loadFitting = useCallback(
    (signal: AbortSignal) => (startedFrom === null ? ALL_FIT : listStartWorkflows(projectId, startedFrom, signal)),
    [projectId, startedFrom],
  );
  const { state: fitting, reload: reloadFitting } = useLoad(loadFitting);
  // Shipped Workflows for one job (editing, memories) start from their own actions; one whose
  // input has its chooser here starts here too (A8: the result to accept is picked).
  // A Workflow the caller asked for stays, even a shipped one ("Run this Workflow"), by item:
  // at another content version it has another exact version. So does the one picked, when
  // documents added after the pick don't fit it: Start then says why.
  const askedFor = intent.workflow === null ? null : itemIdOf(intent.workflow);
  const fits = (candidate: WorkflowSummary) => fitting.status === "loaded" && (fitting.value === null || fitting.value.has(candidate.reference));
  const workflows = listing.status === "loaded"
    ? (listing.value?.workflows ?? []).filter((candidate) => {
        const item = itemIdOf(candidate.reference);
        // An abstract Workflow runs only nested in one that binds its steps (ADR-0043 D1).
        const offered = !candidate.abstract && (candidate.purpose === null || STARTS_HERE.has(candidate.purpose));
        return item === askedFor || item === pickedItem || (offered && fits(candidate));
      })
    : [];
  const workflow = pickedItem === FREE_THREAD ? null : (workflows.find((candidate) => itemIdOf(candidate.reference) === pickedItem) ?? null);
  const prepared = usePreparedStart({ projectId, workflow: workflow?.reference ?? null, bindings, values });
  const routeNeed = useStartRouteNeed(projectId, workflow?.reference ?? null, contentVersion);
  const plan = prepared.status === "loaded" ? prepared.value : prepared.status === "loading" ? prepared.previous : null;
  // A free Thread may work on documents; a Workflow takes them where it binds a subject (F99).
  const takesEntries = workflow === null || (plan?.inputs.some((input) => input.binding === "subject") ?? false);
  const boundEntries = takesEntries ? entries : NO_ENTRIES;
  const blockers = workflow === null || plan === null ? null : startBlockers(plan, t);
  // Start waits for the server's answer on exactly these bindings and values (F101).
  // Whether the start needs an LLM is the server's answer (route options' `needsRoute`), asked before any PC is chosen.
  // Only then does it choose LLMs; its Start waits until the PCs are read, so the first agent step never runs on one nobody saw.
  const choosesRoutes = workflow !== null && routeNeed.status === "loaded" && routeNeed.value;
  // An LLM the start needs and nobody chose, with none to choose until a PC connects: Start asks for it (TR-B5-01, F101).
  const lacksLlm = choosesRoutes && routeDraft === null;
  const ready = workflow === null || (prepared.status === "loaded" && prepared.value.ready && routeNeed.status === "loaded" && (!choosesRoutes || (routeDraft !== undefined && routeDraft !== null)));
  const missingSentences = [blockers?.text ?? null, lacksLlm ? t("threads.prepare.run.missingLlm") : null].filter((sentence) => sentence !== null);
  const missing = ready || missingSentences.length === 0 ? null : missingSentences.join(" ");

  // After a person picks a Workflow, the focus goes to its first input once its plan is shown (F98).
  const { inputsRef, picked } = useFocusOnInputs(prepared.status === "loaded");
  const pick = (item: string) => {
    if (item !== FREE_THREAD) picked();
    onPick(item);
  };
  // After a person picks an entry, Start comes into view (TR-A2-07).
  const startRef = useRef<HTMLDivElement>(null);
  const startPending = useRef(false);
  const changeEntries = (changed: readonly ThreadSubject[], picked: CollectionVersionRef | null) => {
    startPending.current = picked !== null;
    onEntriesChange(changed, picked);
  };
  useEffect(() => {
    if (!startPending.current) return;
    startPending.current = false;
    startRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [entries]);

  if (listing.status !== "loaded") return <LoadStatus state={listing} loadingLabel={t("threads.prepare.run.loadingWorkflows")} onRetry={reload} />;
  const title = runTitle(workflow?.name ?? null, boundEntries, t);
  const starting = submission.status === "submitting" || submission.status === "succeeded";
  // A Workflow picked but not in this content version: Start waits for a choice (F52).
  const chosenMissing = pickedItem !== null && pickedItem !== FREE_THREAD && workflow === null;
  const corpusName = runCorpus === null || corpora?.status !== "loaded" ? null : (corpora.value.find((corpus) => corpus.corpusId === runCorpus.collectionId)?.name ?? null);

  const start = () => {
    submit(async () => {
      const thread = await startRun({
        projectId,
        title,
        workflow: workflow?.reference ?? null,
        contentVersion,
        corpusVersion,
        entries: boundEntries,
        inputValues: workflow === null ? NO_VALUES : values,
        routes: choosesRoutes ? (routeDraft ?? null) : null,
        requestKey,
      });
      onStarted(thread.threadId, choosesRoutes ? routeDraft?.connectorId : undefined);
    });
  };

  const routes = workflow === null || contentVersion === null || !choosesRoutes ? null : { projectId, workflow: workflow.reference, contentVersion, draft: routeDraft, onDraftChange: onRouteDraftChange, disabled: starting };

  return (
    <StartRoutesContext.Provider value={routes}>
    <div className="space-y-5">
      <WorkflowChoice
        projectId={projectId}
        listing={listing.value === null ? null : { ...listing.value, workflows }}
        pending={fitting.status === "loaded" ? null : <LoadStatus state={fitting} loadingLabel={t("threads.prepare.run.loadingWorkflows")} onRetry={reloadFitting} />}
        fitted={startedFrom !== null}
        workflow={workflow}
        cameWithWorkflow={intent.workflow !== null}
        requestKey={requestKey}
        disabled={starting}
        onPick={pick}
      />
      <div ref={inputsRef} tabIndex={-1} className="space-y-5 outline-none">
        {takesEntries ? (
          <section className="space-y-2" aria-labelledby={headingId}>
            <h3 id={headingId} className="app-title text-sm font-semibold">{t("threads.prepare.run.worksOn")}</h3>
            {runCorpus === null ? (
              <p className={FORM_HELP_TEXT_CLASS}>{entries.length === 0 ? t("threads.prepare.run.noCorpus") : entries.map((entry) => entry.label).join(", ")}</p>
            ) : (
              <DocumentSetField
                projectId={projectId}
                entries={entries}
                searchIn={entries.length === 0 ? null : runCorpus}
                onChange={changeEntries}
                disabled={starting}
                needed={blockers?.entries === true ? t("threads.prepare.run.chooseEntry") : null}
              />
            )}
          </section>
        ) : null}
        {boundEntries[0] === undefined ? null : <EarlierRuns projectId={projectId} subject={boundEntries[0]} />}
        {workflow === null ? null : (
          <StartPlan projectId={projectId} state={prepared} values={values} onValuesChange={(changed) => { setGiven({ forItem: pickedItem, values: changed }); }} disabled={starting} />
        )}
      </div>
      <RunVersions projectId={projectId} versions={versions} chosen={version?.reference ?? null} workflow={workflow?.reference ?? null} onChoose={onVersionChange} />
      <p className={FORM_HELP_TEXT_CLASS}>
        {t("threads.prepare.run.title", { title })} {corpusName === null ? null : t("threads.prepare.run.corpus", { corpus: corpusName })}
      </p>
      {submission.status === "failed" ? <ErrorNotice error={submission.error} /> : null}
      {routeNeed.status === "failed" ? <ErrorNotice error={routeNeed.error} /> : null}
      <StartRoutes />
      <div ref={startRef} className="flex flex-wrap items-center justify-end gap-3">
        {missing === null ? null : <p id={`${requestKey}-missing`} className={FORM_HELP_TEXT_CLASS}>{missing}</p>}
        {lacksLlm ? <ConnectorSetupButton inline={true} /> : null}
        <AppButton variant="primary" size="sm" onClick={start} disabled={starting || chosenMissing || !ready} aria-describedby={missing === null ? undefined : `${requestKey}-missing`}>
          {starting ? t("threads.prepare.starting") : t("threads.prepare.run.start")}
        </AppButton>
      </div>
    </div>
    </StartRoutesContext.Provider>
  );
}
